import { makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';
import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

import { spacing } from '@/theme/tokens';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: spacing.m,
    gap: spacing.xxs,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
    padding: `${spacing.s} ${spacing.m}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'background-color 120ms ease, color 120ms ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  linkActive: {
    backgroundColor: tokens.colorNeutralBackground3Selected,
    color: tokens.colorNeutralForeground1,
    fontWeight: 600,
  },
  icon: {
    display: 'inline-flex',
    width: '20px',
    height: '20px',
  },
  badge: {
    marginLeft: 'auto',
  },
  sectionLabel: {
    padding: `${spacing.l} ${spacing.m} ${spacing.xs}`,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
});

export interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
  end?: boolean;
  /** Optional badge rendered after the label — used by the Approvals
   *  item to surface a pending-request count. Kept as a free ReactNode
   *  so callers can choose Fluent's CounterBadge / Badge / plain text
   *  without coupling NavRail to the elevation feature.
   */
  badge?: ReactElement;
}

interface NavRailProps {
  items: NavItem[];
  groupLabel?: string;
}

export function NavRail({ items, groupLabel }: NavRailProps) {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      {groupLabel ? (
        <Text size={100} weight="semibold" className={classes.sectionLabel}>
          {groupLabel}
        </Text>
      ) : null}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            mergeClasses(classes.link, isActive ? classes.linkActive : undefined)
          }
        >
          <span className={classes.icon} aria-hidden>
            {item.icon}
          </span>
          {item.label}
          {item.badge ? <span className={classes.badge}>{item.badge}</span> : null}
        </NavLink>
      ))}
    </div>
  );
}
